import React, { useEffect, useState } from 'react';
import { useHCaptchaContext, usePaymentContext, useRecurly } from '@fff-web/fff-payments';
import { showErrorToast } from '@fff-web/fff-ui-shared/Toast';
import env from '../../utils/env';
import styles from './PaymentInfo.module.scss';
import paypalButtonLogo from './assets/img/paypal-logo-button.png';
import { handleSubmission } from './handleSubmission';
import { useFriendContext } from '../../utils/ContextProviders/FriendContext';
import { useAppContext } from '../../utils/ContextProviders/AppContext';
import useGetSocialEcommContracts from './hooks/useGetSocialEcommContracts';
import SuccessfulAccountCreationModal from './SuccessfulAccountCreationModal';
import checkoutSecureLogo from './assets/img/checkout-secure-logo.png';
import { isMobile } from './paymentInfoUtils';
import { PaymentType } from '../../utils/Data/registerConnectorTypes';

export const PAYPAL = 'PAYPAL';

function PayPal() {
  // set up paypal
  const recurly = useRecurly();
  const payPal = recurly.PayPal({
    display: { displayName: 'My Product' },
    braintree: { clientAuthorization: env.brainTreePublicKey },
  });
  // get everything needed for submission
  const { billingMethod } = usePaymentContext();
  const { hCaptchaToken, setHCaptchaToken, setExecuteHCaptcha } = useHCaptchaContext();
  const [paypalToken, setPaypalToken] = useState<null | string>(null);
  const { friend, setFriend, avatar } = useFriendContext();
  const {
    goToStep, countries, viewSize,
  } = useAppContext();
  const socialEcommContracts = useGetSocialEcommContracts();
  const [showAfterCreation, setShowAfterCreation] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const afterRegister = () => {
    setShowAfterCreation(true);
    setSubmitting(false);
  };

  const handleRegisterFailure = () => {
    setExecuteHCaptcha(false);
    setHCaptchaToken('');
  };

  useEffect(() => {
    payPal.on('token', (token: any, err: any) => {
      if (err) {
        return showErrorToast(
          'Payment Update Failed',
          'Your payment information was not updated. Please try again.',
        );
      }
      return setPaypalToken(token.id);
    });

    payPal.on('error', () => {
      setSubmitting(false);
      setShowAfterCreation(false);
      return showErrorToast(
        'Payment Update Failed',
        'Your payment information was not updated. Please try again.',
      );
    });

    payPal.on('cancel', () => {
      setSubmitting(false);
      setShowAfterCreation(false);
      return showErrorToast(
        'Payment Update Canceled',
        'Your payment information was not updated. Please try again.',
      );
    });
  }, [payPal]);

  useEffect(() => {
    if (
      hCaptchaToken
      && paypalToken
      && billingMethod === PAYPAL
    ) {
      handleSubmission(
        paypalToken,
        friend,
        setFriend,
        goToStep,
        afterRegister,
        handleRegisterFailure,
        avatar,
        PaymentType.PAYPAL,
        socialEcommContracts,
        countries,
        hCaptchaToken,
      );
    }
  }, [paypalToken,
    friend,
    setFriend,
    goToStep,
    afterRegister,
    handleRegisterFailure,
    avatar,
    PaymentType.PAYPAL,
    socialEcommContracts,
    countries,
    hCaptchaToken]);

  const handleOnClick = () => {
    setSubmitting(true);
    setExecuteHCaptcha(true);
    payPal.start();
  };

  const onTimeOut = () => {
    setShowAfterCreation(false);
  };
  return (
    <>
      <div className={styles.paypalWrapper}>
        <p className={styles.disclaimer}>
          By
          {' '}
          {isMobile(viewSize) ? 'tapping' : 'clicking'}
          {' '}
          ‘Save &amp; Submit with Paypal’, you agree to our
          {' '}
          <a href="https://legal.fabfitfun.com/#terms-of-use" target="_blank" rel="noreferrer">
            Terms of Use and Sale
          </a>
          {' '}
          and our
          {' '}
          <a href="https://legal.fabfitfun.com/#shop-with-friends-terms" target="_blank" rel="noreferrer">Shop with Friends Terms</a>
          .
        </p>
        <button
          type="button"
          disabled={submitting}
          className={styles.paypalBtn}
          onClick={handleOnClick}
        >
          SAVE &amp; SUBMIT WITH
          <img src={paypalButtonLogo} alt="Pay with PayPal" className={styles.paypalBtnLogo} />
        </button>
        <div className={styles.checkoutLogoContainer}>
          <img src={checkoutSecureLogo} alt="Checkout secure" className={styles.checkoutSecureLogo} />
        </div>
      </div>
      { showAfterCreation && <SuccessfulAccountCreationModal onTimeOut={onTimeOut} /> }
    </>
  );
}

export default PayPal;
